import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CircleAvatar } from "@/components/CircleAvatar";
import { api } from "@/lib/api";
import { normRecipient, money, UNLIMITED_RADIUS } from "@/lib/blessed";

// Modal that lists nearby recipients and routes to the donate flow when one is picked.
export const BlessPicker = ({ open, onOpenChange, amount, message, coords, title = "Who do you want to bless?" }) => {
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const stored = localStorage.getItem("blessed_radius");
    const rad = stored !== null ? JSON.parse(stored) : 100;
    const params = { limit: 20, radius_miles: rad == null ? UNLIMITED_RADIUS : rad };
    if (coords) {
      params.lat = coords.lat;
      params.lng = coords.lng;
    }
    api
      .get("/recipients/nearby", { params })
      .then(({ data }) => {
        const list = (Array.isArray(data) ? data : data.recipients || data.items || [])
          .map(normRecipient)
          .filter((r) => !r.isMe);
        setPeople(list);
      })
      .catch(() => setPeople([]))
      .finally(() => setLoading(false));
  }, [open, coords]);

  const pick = (r) => {
    const q = new URLSearchParams();
    if (amount) q.set("amount", String(amount));
    if (message) q.set("message", message);
    onOpenChange(false);
    navigate(`/donate/${r.id}?${q.toString()}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bless-picker" className="max-w-md bg-surface-2 border-border rounded-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-black flex items-center justify-between">
            {title}
            {amount ? <span className="text-sm px-3 py-1 rounded-pill bg-brand-primary text-white">{money(amount)}</span> : null}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto slim-scroll -mx-2 px-2">
          {loading && <p className="text-center text-sm text-ink-tertiary py-8">Finding neighbors near you…</p>}
          {!loading && people.length === 0 && (
            <p className="text-center text-sm text-ink-tertiary py-8">No neighbors found nearby right now.</p>
          )}
          <div className="space-y-2">
            {people.map((r) => (
              <button
                key={r.id}
                data-testid={`picker-person-${r.id}`}
                onClick={() => pick(r)}
                className="w-full flex items-center gap-3 p-3 rounded-md bg-surface hover:bg-surface-3 transition-colors text-left"
              >
                <CircleAvatar src={r.picture} name={r.name} size={44} ring={r.hasStory ? "linear-gradient(135deg,#1E40AF,#8B5CF6)" : undefined} />
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold truncate">{r.name}</div>
                  {r.story && <div className="text-xs text-ink-tertiary truncate">{r.story}</div>}
                </div>
                {r.distance != null && (
                  <span className="flex items-center gap-1 text-xs font-bold text-ink-tertiary shrink-0">
                    <MapPin size={12} /> {r.distance.toFixed(1)}mi
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
